import { Avatar, Grid, List, Stack, Typography } from '@mui/material';
import {
  exportToPdf,
  getOnceIndOptions,
  IMedicationHistory,
  IOnceIndOptions,
  searchMedicationHistory,
} from 'API';
import { CreateMedicineIcon, PurpleSearchIcon } from 'Images';
import { useEffect, useState } from 'react';
import { MedicationHistoryItem } from './MedicationHistoryitem';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { ORDER_STATUS } from 'Context';
import { ControlledAutocomplete, Loader } from 'Components';
import { sendEvent } from 'Utils/sendEvent';
import { ButtonActionNew } from 'Components/ButtonActionNew';

export const MedicationHistoryList = () => {
  const [medicationName, setMedicationName] = useState<string | null>(null);

  const [historyMedication, setHistoryMedication] = useState<
    IMedicationHistory[]
  >([]);

  const [onceIndOptions, setOnceIndOptions] = useState<IOnceIndOptions[]>([]);

  const dictionary = useDictionary(Dictionary, DICTIONARY.MedicationsHistory);

  const initMedicationHistoryAndOnceIndOptions = async () => {
    setIsLoading('IN_PROGRESS');
    const medicationHistoryData = await searchMedicationHistory();
    if (medicationHistoryData) {
      setHistoryMedication(medicationHistoryData);
    }
    //get dictionary of codes and texts for const med/ one time med
    const onceIndOptions: IOnceIndOptions[] = await getOnceIndOptions();
    if (onceIndOptions) {
      setOnceIndOptions(onceIndOptions);
    }
    setIsLoading('FINISHED');
  };

  const [isLoading, setIsLoading] = useState<ORDER_STATUS>('STARTED');
  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  useEffect(() => {
    initMedicationHistoryAndOnceIndOptions();
  }, [imposterId]);

  const exportToPdfHandler = async () => {
    sendEvent('Medicine', 'MedicineReport');
    await exportToPdf();
  };

  const isDesktop = useIsDesktop();

  const customStyleBtn = {
    borderColor: 'primary.shade_400',
    width: isDesktop ? '205px' : '102px',
    p: isDesktop ? '16px 12px' : '12px 10px',
    boxShadow: '0px 1px 22px 0px rgba(21, 82, 232, 0.12)',
    mb: 5,
  };

  if (isLoading !== 'FINISHED') {
    return <Loader />;
  }

  return (
    <>
      {isDesktop ? (
        <Grid container justifyContent='space-between' mb={5}>
          <Grid container item xs={6}>
            <ButtonActionNew
              imageSrc={CreateMedicineIcon}
              text={dictionary.exportToPDF}
              callback={exportToPdfHandler}
              sx={customStyleBtn}
            />

            <ControlledAutocomplete
              freeSolo
              label={dictionary.searchMedicationByName}
              options={historyMedication
                .map((medicationHistory) => medicationHistory.medicationName)
                .sort((a, b) => (a > b ? 1 : -1))}
              setValue={(value: string | null) => {
                setMedicationName(value);
              }}
              setInputValue={(value) => setMedicationName(value)}
              paperComponent={
                historyMedication
                  .map((medicationHistory) => medicationHistory.medicationName)
                  .sort((a, b) => (a > b ? 1 : -1)).length === 0 && (
                  <Typography variant={'body3'}>
                    {
                      Dictionary[DICTIONARY.MedicalReferrals].search
                        .advancedSearch.noResult
                    }
                  </Typography>
                )
              }
              paperSx={{
                mt: '1px',
                ml: '10px',
                width: isDesktop ? '425px' : '93%',
              }}
              style={{
                borderRadius: '34px',
                border: '1px solid',
                borderColor: 'primary.shade_200',

                '&&& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '34px',
                  bgcolor: '#fff',
                  zIndex: -1,
                  borderColor: 'primary.shade_200',
                  borderWidth: '1px',
                },
                '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
                  borderColor: 'transparent',
                },
              }}
              disableClearIcon
              endAdornment={
                <Stack
                  direction={'row'}
                  marginRight={'5px'}
                  width={'auto'}
                  gap={'15px'}
                >
                  <Avatar
                    alt={''}
                    src={PurpleSearchIcon}
                    sx={{
                      width: isDesktop ? '40px' : '34px',
                      height: isDesktop ? '40px' : '34px',
                    }}
                  />
                </Stack>
              }
            />
          </Grid>
        </Grid>
      ) : (
        <Grid container mb={4}>
          <ButtonActionNew
            imageSrc={CreateMedicineIcon}
            text={dictionary.exportToPDF}
            callback={exportToPdfHandler}
            sx={customStyleBtn}
          />

          <Grid container item>
            <ControlledAutocomplete
              freeSolo
              label={dictionary.searchMedicationByName}
              options={historyMedication
                .map((medicationHistory) => medicationHistory.medicationName)
                .sort((a, b) => (a > b ? 1 : -1))}
              setValue={(value: string | null) => {
                setMedicationName(value);
              }}
              setInputValue={(value) => setMedicationName(value)}
              paperComponent={
                historyMedication
                  .map((medicationHistory) => medicationHistory.medicationName)
                  .sort((a, b) => (a > b ? 1 : -1)).length === 0 && (
                  <Typography variant={'body3'}>
                    {
                      Dictionary[DICTIONARY.MedicalReferrals].search
                        .advancedSearch.noResult
                    }
                  </Typography>
                )
              }
              paperSx={{
                borderRadius: '0 0 22px 22px',
                outline: '0px',
                border: '1px solid',
                borderColor: 'primary.shade_200',
                borderTop: '0px',
                boxShadow: 'none',
                '& ul': {
                  maxHeight: '250px',
                  bgcolor: '#fff',
                  top: '-7px',
                  position: 'relative',
                },
              }}
              style={{
                width: '100%',
                m: '0 auto',
                borderRadius: '22px',
                bgcolor: '#fff',

                '&&&:has(input[dropdown="open"])': {
                  borderRadius: '16px 16px 0 0',
                },
                '&&& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '22px',
                  borderColor: 'primary.shade_200',
                  borderWidth: '1px',
                },
                '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
                  borderColor: 'primary.shade_200',
                  borderRadius: '16px 16px 0 0',
                  borderBottom: '0',
                  borderWidth: '1px',
                },
                '&&& .MuiInputBase-root input[dropdown="closed"] ~ fieldset': {
                  border: '1px solid',
                  borderColor: 'primary.shade_200',
                },

                '&&& .MuiInputLabel-root': {
                  paddingX: '8px',
                  paddingY: isDesktop ? '4px' : '0px',
                },
                '&&& .MuiInputLabel-shrink': {
                  padding: '0',
                },
                '&&& .MuiOutlinedInput-root': {
                  height: '47.50px',
                  borderRadius: '0px',
                },
                '&&& .MuiOutlinedInput-input': {
                  padding: '0 15px',
                },
              }}
              disableClearIcon
              endAdornment={
                <Stack
                  direction={'row'}
                  marginRight={'5px'}
                  width={'auto'}
                  gap={'15px'}
                >
                  <Avatar
                    alt={''}
                    src={PurpleSearchIcon}
                    sx={{
                      width: isDesktop ? '40px' : '34px',
                      height: isDesktop ? '40px' : '34px',
                    }}
                  />
                </Stack>
              }
            />
          </Grid>
        </Grid>
      )}
      <List sx={{ mb: '96px' }}>
        {historyMedication
          .filter((medicationHistory) => {
            if (medicationName === null) {
              return true;
            } else {
              return medicationHistory.medicationName === medicationName;
            }
          })
          .map((medicationHistory, index) => {
            return (
              <MedicationHistoryItem
                key={index}
                medicationHistory={medicationHistory}
                onceIndOptions={onceIndOptions}
              />
            );
          })}
      </List>
    </>
  );
};
